import { type BrandConfig } from '@sso/brand-configs';
import { UrlParams } from '@sso/common-types';

import { getBrowserGlobals } from '../helpers/globals';
import { realms, socialApps } from '../shared/constants';
import { isMobileDevice } from './device';
import { getMigrationAppFromUrlParams, isAppMigrationBrand } from './migrationBrands';

type SocialAppEnabledParams = {
  market: string;
  realm: string;
  isMigrationBrand: boolean;
  brandEnabledSocial: Array<string>;
  linkAccount?: string;
  utmSource?: string;
  page?: string;
  showFacebookCreateAccount?: boolean;
  isChina?: boolean;
};

export type EnabledSocialSites = {
  page: string;
  market: string;
  realm: string;
  isChina: boolean;
  urlParams: UrlParams;
  brandConfig: BrandConfig;
};

function getBrandEnabledSocial(page: string, brandConfig: BrandConfig) {
  const browserGlobals = getBrowserGlobals();
  let brandConfigSource = brandConfig;

  if (browserGlobals != null && browserGlobals.window.sso?.brand_config != null) {
    brandConfigSource = browserGlobals.window.sso.brand_config;
  }

  if (page === 'login') {
    const { social = [], disabledSocial = [] } = brandConfigSource?.login?.display ?? {};
    return social.filter(socialApp => disabledSocial.includes(socialApp) === false);
  }

  if (page === 'create') {
    const { social = [] } = brandConfigSource?.create?.display ?? {};
    return social;
  }

  return [];
}

function isWechatEnabled({ market, realm, isMigrationBrand, brandEnabledSocial, linkAccount }: SocialAppEnabledParams) {
  const wechatEnabledCountries = ['SG', 'CN'];
  const country = market?.split('_')[1] ?? '';

  const brandEligible = realm === realms.IDP && (brandEnabledSocial.includes(socialApps.wechat) || isMigrationBrand);
  const marketEligible = wechatEnabledCountries.includes(country);
  const pageEligible = linkAccount !== socialApps.wechat;
  const deviceEligible = !isMobileDevice();

  return brandEligible === true && marketEligible && pageEligible && deviceEligible;
}

function isAmazonEnabled({
  market,
  realm,
  isMigrationBrand,
  utmSource,
  page,
  brandEnabledSocial,
  linkAccount = '',
}: SocialAppEnabledParams) {
  const brandEligible = realm === realms.IDP && (brandEnabledSocial.includes(socialApps.amazon) || isMigrationBrand);
  const marketEligible = market === 'en_US';
  const pageEligible =
    linkAccount !== socialApps.amazon &&
    (page === 'login_pin_page' || page === 'login' || utmSource === 'AmazonBusinessPrime');
  return brandEligible && marketEligible && pageEligible;
}

function isAppleEnabled({
  realm,
  isChina,
  isMigrationBrand,
  brandEnabledSocial,
  linkAccount = '',
}: SocialAppEnabledParams) {
  const brandEligible = realm === realms.IDP && (brandEnabledSocial.includes(socialApps.apple) || isMigrationBrand);
  const marketEligible = isChina !== true;
  const pageEligible = linkAccount !== socialApps.apple;

  return brandEligible && marketEligible && pageEligible;
}

function isFacebookEnabled({
  realm,
  isChina,
  isMigrationBrand,
  brandEnabledSocial,
  showFacebookCreateAccount = true,
  linkAccount = '',
}: SocialAppEnabledParams) {
  const brandEligible = realm === realms.IDP && (brandEnabledSocial.includes(socialApps.facebook) || isMigrationBrand);
  const marketEligible = isChina !== true;
  const pageEligible = linkAccount !== socialApps.facebook;
  const featureFlagEligible = showFacebookCreateAccount;
  return brandEligible && marketEligible && pageEligible && featureFlagEligible;
}

function isGoogleEnabled({
  market,
  realm,
  isChina,
  isMigrationBrand,
  brandEnabledSocial,
  linkAccount = '',
}: SocialAppEnabledParams) {
  const googleDisabledMarkets = ['SG'];
  const country = market?.split('_')[1] ?? '';

  const brandEligible = realm === realms.IDP && (brandEnabledSocial.includes(socialApps.google) || isMigrationBrand);
  const marketEligible = isChina !== true && !googleDisabledMarkets.includes(country);
  const pageEligible = linkAccount !== socialApps.google;

  return brandEligible && marketEligible && pageEligible;
}

export function getEnabledSocialSites({ page, market, realm, isChina, urlParams, brandConfig }: EnabledSocialSites) {
  const isMigrationBrand = isAppMigrationBrand(getMigrationAppFromUrlParams(urlParams) ?? '');
  const brandEnabledSocial = getBrandEnabledSocial(page, brandConfig);

  return {
    [socialApps.wechat]: isWechatEnabled({
      market,
      realm,
      isMigrationBrand,
      brandEnabledSocial,
      linkAccount: urlParams?.link_account,
    }),
    [socialApps.amazon]: isAmazonEnabled({
      market,
      realm,
      isMigrationBrand,
      utmSource: urlParams?.utm_source ?? '',
      page,
      brandEnabledSocial,
      linkAccount: urlParams?.link_account,
    }),
    [socialApps.facebook]: isFacebookEnabled({
      market,
      realm,
      isChina,
      isMigrationBrand,
      brandEnabledSocial,
      linkAccount: urlParams?.link_account,
    }),
    [socialApps.google]: isGoogleEnabled({
      market,
      realm,
      isChina,
      isMigrationBrand,
      brandEnabledSocial,
      linkAccount: urlParams?.link_account,
    }),
    [socialApps.apple]: isAppleEnabled({
      market,
      realm,
      isChina,
      isMigrationBrand,
      brandEnabledSocial,
      linkAccount: urlParams?.link_account,
    }),
  };
}
